import type { LucideIcon } from 'lucide-react';
import {
  Cog,
  FlaskConical,
  Hammer,
  KeyRound,
  Lightbulb,
  MessageSquare,
  Monitor,
  ShoppingCart,
  Wrench,
} from 'lucide-react';
import { formatError } from '@/lib/errors';
import type { Json, TicketEventKind, TicketPriority, TicketStatus } from '@/types/database';

/**
 * Display metadata for Tickets (migration 143). Mirrors
 * features/projects/display.ts: brand accents stay wayfinding-only —
 * chips, dots, accent bars — never ambient fills.
 */

export interface TicketStatusMeta {
  label: string;
  /** What the status means, shown as a hint in pickers and the tablet. */
  hint: string;
  /** Chip fill + text colour. */
  className: string;
  /** Solid swatch for dots. */
  dot: string;
  order: number;
  closed: boolean;
}

export const TICKET_STATUS_META: Record<TicketStatus, TicketStatusMeta> = {
  new: {
    label: 'New',
    hint: 'Nobody has looked at this yet.',
    className: 'border border-dashed border-border bg-transparent text-muted-foreground',
    dot: 'border border-dashed border-muted-foreground bg-transparent',
    order: 0,
    closed: false,
  },
  acknowledged: {
    label: 'Seen',
    hint: 'Someone has it and will get to it.',
    className: 'bg-muted text-foreground',
    dot: 'bg-muted-foreground/60',
    order: 1,
    closed: false,
  },
  in_progress: {
    label: 'In progress',
    hint: 'Being worked on right now.',
    className: 'bg-atmosphere-dark text-linen',
    dot: 'bg-atmosphere-dark',
    order: 2,
    closed: false,
  },
  on_order: {
    label: 'On order',
    hint: 'Ordered — waiting for it to arrive.',
    className: 'bg-info-soft/40 text-info',
    dot: 'bg-info',
    order: 3,
    closed: false,
  },
  waiting: {
    label: 'Waiting',
    hint: 'Waiting on the requester or someone outside.',
    className: 'bg-fresh-salmon/30 text-fresh-salmon-dark',
    dot: 'bg-fresh-salmon',
    order: 4,
    closed: false,
  },
  done: {
    label: 'Done',
    hint: 'Finished.',
    className: 'bg-bright-moss-dark text-linen',
    dot: 'bg-bright-moss-dark',
    order: 5,
    closed: true,
  },
  declined: {
    label: 'Declined',
    hint: 'Not going ahead — see the reason.',
    className: 'bg-muted text-muted-foreground line-through decoration-muted-foreground/60',
    dot: 'bg-muted-foreground/40',
    order: 6,
    closed: true,
  },
  cancelled: {
    label: 'Cancelled',
    hint: 'Withdrawn by the requester.',
    className: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/40',
    order: 7,
    closed: true,
  },
};

export const TICKET_STATUSES: TicketStatus[] = [
  'new',
  'acknowledged',
  'in_progress',
  'on_order',
  'waiting',
  'done',
  'declined',
  'cancelled',
];

export const OPEN_TICKET_STATUSES: TicketStatus[] = TICKET_STATUSES.filter(
  (s) => !TICKET_STATUS_META[s].closed,
);
export const CLOSED_TICKET_STATUSES: TicketStatus[] = TICKET_STATUSES.filter(
  (s) => TICKET_STATUS_META[s].closed,
);

/** Statuses the handling side (triage / assignee) may pick. */
export const HANDLER_STATUS_OPTIONS: TicketStatus[] = [
  'acknowledged',
  'in_progress',
  'on_order',
  'waiting',
  'done',
  'declined',
];

export function isTicketClosed(status: TicketStatus): boolean {
  return TICKET_STATUS_META[status].closed;
}

export interface TicketPriorityMeta {
  label: string;
  className: string;
  dot: string;
  order: number;
}

export const TICKET_PRIORITY_META: Record<TicketPriority, TicketPriorityMeta> = {
  low: {
    label: 'Low',
    className: 'bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground/40',
    order: 0,
  },
  medium: {
    label: 'Medium',
    className: 'bg-atmosphere/40 text-atmosphere-dark',
    dot: 'bg-atmosphere-dark',
    order: 1,
  },
  high: {
    label: 'High',
    className: 'bg-fresh-salmon/40 text-fresh-salmon-dark',
    dot: 'bg-fresh-salmon',
    order: 2,
  },
  urgent: {
    label: 'Urgent',
    className: 'bg-fresh-salmon-dark text-linen',
    dot: 'bg-fresh-salmon-dark',
    order: 3,
  },
};

export const TICKET_PRIORITIES: TicketPriority[] = ['low', 'medium', 'high', 'urgent'];

/** T-0042 */
export function ticketRef(number: number | null | undefined): string {
  return `T-${String(number ?? 0).padStart(4, '0')}`;
}

/** Display name for a profile embed, falling back to email, then to `fallback`. */
export function personLabel(
  p: { full_name: string | null; email: string } | null | undefined,
  fallback = 'Former teammate',
): string {
  if (!p) return fallback;
  return p.full_name?.trim() || p.email;
}

/**
 * Icon per seeded category slug (migration 143). Unknown slugs fall back
 * to the generic speech bubble so admin-created categories still render.
 */
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  tool: Wrench,
  fixture: Hammer,
  purchase: ShoppingCart,
  maintenance: Cog,
  access: KeyRound,
  it: Monitor,
  prototype: FlaskConical,
  idea: Lightbulb,
  other: MessageSquare,
};

export function ticketCategoryIcon(slug: string | null | undefined): LucideIcon {
  return (slug && CATEGORY_ICONS[slug]) || MessageSquare;
}

/** Shape of ticket_events.meta as written by the triggers. */
interface TicketEventMeta {
  auto?: boolean;
  assignee_name?: string | null;
  from_name?: string | null;
  to_name?: string | null;
  resolution?: string | null;
  task_name?: string | null;
  project_id?: string | null;
  attachment_id?: string;
  bucket?: string;
  path?: string;
}

function metaOf(meta: Json | null | undefined): TicketEventMeta {
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    return meta as TicketEventMeta;
  }
  return {};
}

function statusLabel(value: string | null): string {
  if (!value) return '';
  return value in TICKET_STATUS_META
    ? TICKET_STATUS_META[value as TicketStatus].label
    : value;
}

function priorityLabel(value: string | null): string {
  if (!value) return '';
  return value in TICKET_PRIORITY_META
    ? TICKET_PRIORITY_META[value as TicketPriority].label
    : value;
}

/**
 * One line of plain English per activity event, without the actor
 * ("changed status from New to In progress"). The timeline prefixes the
 * actor name itself so it can style it.
 */
export function describeTicketEvent(e: {
  kind: TicketEventKind;
  from_value: string | null;
  to_value: string | null;
  meta: Json;
}): string {
  const m = metaOf(e.meta);
  switch (e.kind) {
    case 'created':
      return 'opened this request';
    case 'status_changed':
      return `moved it from ${statusLabel(e.from_value)} to ${statusLabel(e.to_value)}`;
    case 'assigned':
      if (m.auto) return `was routed to ${m.assignee_name ?? 'someone'} automatically`;
      if (m.from_name) return `handed it from ${m.from_name} to ${m.assignee_name ?? 'someone'}`;
      return `routed it to ${m.assignee_name ?? 'someone'}`;
    case 'unassigned':
      return `took it back from ${m.from_name ?? 'the assignee'}`;
    case 'priority_changed':
      return `set priority to ${priorityLabel(e.to_value)}`;
    case 'category_changed':
      return m.to_name ? `filed it under ${m.to_name}` : 'cleared the category';
    case 'needed_by_changed':
      return e.to_value ? `set needed-by to ${e.to_value}` : 'cleared the needed-by date';
    case 'title_changed':
      return `renamed it to “${e.to_value ?? ''}”`;
    case 'attachment_added':
      return `attached ${e.to_value ?? 'a file'}`;
    case 'linked_task':
      return m.task_name ? `added it to a task list as “${m.task_name}”` : 'unlinked the task';
    case 'resolution_changed':
      return 'updated the resolution';
    default:
      return 'updated this request';
  }
}

/** Events worth showing a requester on the tablet / in the condensed feed. */
export const TIMELINE_EVENT_KINDS: TicketEventKind[] = [
  'created',
  'status_changed',
  'assigned',
  'unassigned',
  'priority_changed',
  'category_changed',
  'needed_by_changed',
  'title_changed',
  'attachment_added',
  'linked_task',
  'resolution_changed',
];

/**
 * The BEFORE UPDATE trigger raises short machine codes; translate them
 * for toasts. Anything else falls through to the raw message.
 */
const TICKET_ERRORS: Record<string, string> = {
  ticket_forbidden: 'Only the requester, the assignee or a manager can change this ticket.',
  ticket_forbidden_field:
    'Only the person handling this ticket (or a manager) can change who has it, the resolution or the linked task.',
  ticket_requester_status:
    'As the requester you can cancel (or un-cancel) this request, but only the person handling it can move it along.',
  ticket_category_org: 'That category does not belong to your organisation.',
  ticket_assignee_org: 'That person is not an active teammate.',
  ticket_requester_org: 'That requester is not part of your organisation.',
  ticket_requester_required: 'Pick who this request is for.',
  ticket_title_required: 'Give the request a short title.',
  ticket_not_found: 'That ticket no longer exists.',
};

export function describeTicketError(err: unknown): string {
  // formatError understands PostgrestError, which is a plain object (not
  // an Error) and would otherwise stringify to "[object Object]".
  const message = formatError(err, '');
  for (const [code, text] of Object.entries(TICKET_ERRORS)) {
    if (message.includes(code)) return text;
  }
  return message || 'Something went wrong.';
}
